import React, { useCallback } from 'react';
import {
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Theme,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { makeStyles, createStyles } from '@mui/styles';
import { firestoreInstance } from 'config/ApiService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import MaterialTable, { MTableToolbar } from 'material-table';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { useNavigate } from 'react-router-dom';
import { MultiFilter } from 'views/triage/components/Filter';
import DirectReferralDialog from 'views/patient/components/DirectReferralDialog';
import { AGREEMENT_TYPE, ClinicalServicesClient } from 'components/ClinicalServicesDropdown';
import { dataQuery } from 'utils/remote-fetch';
import { logGoogleAnalyticsEvent } from 'utils/gaLog';
import { setUserActivity } from 'services/trackUserActivity';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
            color: '#fff'
        }
    })
);

export const USE_CASE_LIST = [
    { id: 'all', name: 'All' },
    { id: 'cash_plan', name: 'Cash Plan' },
    { id: 'primary_care', name: 'Primary Care' },
    { id: 'secondary_care', name: 'Secondary Care' },
    { id: 'waiting_list', name: 'Waiting List' }
];

export const USE_CASE_COLORS: Record<string, string> = {
    cash_plan: '#4caf50',
    primary_care: '#2196f3',
    secondary_care: '#ff9800',
    waiting_list: '#f44336'
};

const PhioengagePatientList = () => {
    const classes = useStyles();
    const client = useSelector((state: RootState) => state.AuthReducer.organisation);
    const clinician = useSelector((state: RootState) => state.AuthReducer.user);
    const permissions = useSelector((state: RootState) => state.AuthReducer.permissions);
    const [filterList, setFilterList] = React.useState<ClinicalServicesClient[]>([]);
    const [openDirectReferral, setOpenDirectReferral] = React.useState(false);
    const [working, setIsWorking] = React.useState(false);
    const region = useSelector((state: RootState) => state.AuthReducer.currentRegion);

    // filters
    const [clientFilter, setClientFilter] = React.useState<string[]>(['all']);
    const [usecaseFilter, setUsecaseFilter] = React.useState<string[]>(['all']);
    const [statusFilter, setStatusFilter] = React.useState<string[]>(['all']);

    const mtTableRef = React.useRef<any>(null);

    const navigate = useNavigate();

    const handleClose = () => {
        setIsWorking(false);
    };

    const logGaEvent = (eventName: string, rowData: any) => {
        logGoogleAnalyticsEvent(eventName, {
            region
        });
    };

    const isInternalClinicalServices = permissions.includes('internal_clinical_services');

    const applyCustomFilterToResults = (data: any[]) => {
        if (isInternalClinicalServices && clientFilter.includes('all')) {
            const clientIds = filterList.map((c) => c.id);
            // filter the data based on the clientIds
            const output = data.filter((item) => clientIds.includes(item.customer_id));
            return output;
        }
        return data;
    };

    const buildQueryConstraints = React.useCallback(() => {
        const qConstraints: any[] = [];
        if (isInternalClinicalServices && usecaseFilter.length && !usecaseFilter.includes('all')) {
            qConstraints.push({ name: 'customer_preset', op: 'in', value: usecaseFilter });
        }
        if (
            isInternalClinicalServices &&
            clientFilter &&
            clientFilter.length > 0 &&
            !clientFilter.includes('all')
        ) {
            qConstraints.push({ name: 'customer_id', op: 'in', value: clientFilter });
        }
        if (!isInternalClinicalServices) {
            qConstraints.push({ name: 'customer_id', op: '==', value: client.id });
        }
        if (statusFilter.length > 0 && !statusFilter.includes('all')) {
            qConstraints.push({ name: 'case_status', op: 'in', value: statusFilter });
        }
        if (statusFilter.length === 1 && statusFilter.includes('all')) {
            qConstraints.push({ name: 'case_status', op: 'in', value: ['active', 'not_started'] });
        }
        return qConstraints;
    }, [isInternalClinicalServices, usecaseFilter, clientFilter, client.id, statusFilter]);

    React.useEffect(() => {
        (async () => {
            const ref = collection(firestoreInstance(), 'clients');
            const q = query(
                ref,
                where('agreement_type', 'in', [
                    AGREEMENT_TYPE.ENGAGE,
                    AGREEMENT_TYPE.ACCESS_AND_ENGAGE,
                    AGREEMENT_TYPE.ACCESS_ENGAGE_AND_COLLECT
                ]),
                where('clinical_services_enabled', '==', true)
            );
            const snapshots = await getDocs(q);
            const clients: ClinicalServicesClient[] = [];
            snapshots.forEach((doc) => {
                const data = doc.data();
                clients.push({
                    id: doc.id,
                    name: data.name,
                    agreement_type: data.agreement_type
                });
            });
            clients.sort((a, b) => a.name.localeCompare(b.name));
            clients.unshift({
                id: 'all',
                name: 'All'
            } as ClinicalServicesClient);
            setFilterList(clients);
        })();
    }, []);

    const handleCustomerFilter = useCallback((val: string[]) => {
        setClientFilter(val);
    }, []);

    const handleUsecaseFilter = useCallback((val: string[]) => {
        setUsecaseFilter(val);
    }, []);

    const handleStatusFilter = useCallback((val: string[]) => {
        setStatusFilter(val);
    }, []);

    React.useEffect(() => {
        if (mtTableRef.current) {
            // eslint-disable-next-line no-unused-expressions
            mtTableRef.current.onQueryChange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mtTableRef.current, clientFilter, client, usecaseFilter, statusFilter, openDirectReferral]);

    // Log Page View
    React.useEffect(() => {
        import('utils/gaLog').then(({ logScreenView }) => {
            logScreenView('enrolled_page_view');
        });
    }, []);

    return (
        <Box minWidth="650px">
            <Box mt={2} mb={2}>
                {clinician && (
                    <Grid
                        size={{ xs: 6, lg: 3 }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}
                    >
                        <Button
                            onClick={() => {
                                logGoogleAnalyticsEvent('enrolled_direct_referral_opened');
                                setOpenDirectReferral(true);
                            }}
                            variant="contained"
                        >
                            Add direct referral
                        </Button>
                    </Grid>
                )}
            </Box>
            <MaterialTable
                title={`Enrolled Patients List - ${
                    isInternalClinicalServices
                        ? 'All Internal Clinical Service Customers'
                        : client.name
                }`}
                tableRef={mtTableRef}
                data={(query) =>
                    dataQuery(query, {
                        remoteCollectionName: 'phioengage_user_profiles',
                        fields: buildQueryConstraints(),
                        customSort: applyCustomFilterToResults
                    })
                }
                columns={[
                    {
                        title: 'UID',
                        field: 'id',
                        sorting: false,
                        render: (rowData: any) => (
                            <Typography variant="body2">{rowData.id}</Typography>
                        )
                    },
                    {
                        title: 'Patient',
                        field: 'first_name',
                        sorting: false,
                        render: (rowData: any) => (
                            <Typography variant="body2" color="textSecondary">
                                {rowData.first_name + ' ' + rowData?.last_name}
                            </Typography>
                        )
                    },
                    {
                        title: 'Email',
                        field: 'email',
                        sorting: false
                    },

                    {
                        title: 'Customer',
                        field: 'customer_name',
                        sorting: false,
                        hidden: !permissions.includes('internal_clinical_services'),
                        render: (rowData: any) => {
                            if (!rowData.customer_name) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.customer_name}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Programme',
                        field: 'programme_name',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.programme_name) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.programme_name}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Use Case',
                        field: 'customer_use_case',
                        sorting: false,
                        hidden: !permissions.includes('internal_clinical_services'),
                        render: (rowData: any) => {
                            if (!rowData.customer_preset) {
                                return '-';
                            }
                            return (
                                <Chip
                                    label={rowData.customer_preset.split('_').join(' ')}
                                    size="small"
                                    sx={{
                                        textTransform: 'capitalize',
                                        border: `2px solid ${
                                            USE_CASE_COLORS[`${rowData.customer_preset}`]
                                        }`
                                    }}
                                    variant="outlined"
                                />
                            );
                        }
                    },
                    {
                        title: 'Status',
                        field: 'case_status',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.case_status) {
                                return '';
                            }
                            return (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ textTransform: 'capitalize' }}
                                >
                                    {rowData.case_status.split('_').join(' ')}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Triage Clinician',
                        field: 'triage_clinician',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.triage_clinician) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.triage_clinician}
                                </Typography>
                            );
                        }
                    }
                ]}
                options={{
                    pageSize: 20,
                    pageSizeOptions: [20, 40, 60],
                    searchFieldVariant: 'outlined',
                    filtering: false,
                    showFirstLastPageButtons: false,
                    paging: true
                }}
                components={{
                    Toolbar: (props) => (
                        <Box m={1}>
                            <MTableToolbar {...props} />
                            <Container maxWidth="xl">
                                <Grid container spacing={2} marginTop={1}>
                                    {permissions.includes('internal_clinical_services') && (
                                        <Grid size={{ xs: 6, lg: 3 }}>
                                            <MultiFilter
                                                value={clientFilter ? clientFilter : []}
                                                options={filterList.map((c) => ({
                                                    id: c.id,
                                                    name: c.name
                                                }))}
                                                onChange={handleCustomerFilter}
                                                label="Customer"
                                                key="client-filter"
                                            />
                                        </Grid>
                                    )}
                                    {permissions.includes('internal_clinical_services') && (
                                        <Grid size={{ xs: 6, lg: 3 }}>
                                            <MultiFilter
                                                value={usecaseFilter}
                                                options={USE_CASE_LIST}
                                                onChange={handleUsecaseFilter}
                                                label="Use Case"
                                                key="usecase-filter"
                                            />
                                        </Grid>
                                    )}

                                    <Grid size={{ xs: 6, lg: 3 }}>
                                        <MultiFilter
                                            value={statusFilter}
                                            options={[
                                                {
                                                    id: 'active',
                                                    name: 'Active'
                                                },
                                                {
                                                    id: 'not_started',
                                                    name: 'Not Started'
                                                },
                                                { id: 'all', name: 'All' }
                                            ]}
                                            onChange={handleStatusFilter}
                                            label="Case Status"
                                            key="status-filter"
                                        />
                                    </Grid>
                                </Grid>
                            </Container>
                        </Box>
                    )
                }}
                actions={[
                    (rowData: any) => ({
                        icon: 'visibility',
                        tooltip: 'View Patient',
                        onClick: () => {
                            // add event to log view patient from worklist
                            logGaEvent('enrolled_list_view_patient_click', rowData);
                            navigate(`/dashboard/phioengage-profile/${rowData.id}`);
                            setUserActivity({
                                patientId: rowData.id,
                                eventName: 'details_screen_visited',
                                eventDetails: { visited_from: 'enrolled_patients_list' }
                            });
                        }
                    })
                ]}
                localization={{
                    toolbar: {
                        searchPlaceholder: 'UID, First/Last name, email, Customer, Programme'
                    },
                    pagination: {
                        labelRowsPerPage: '',
                        labelDisplayedRows: ''
                    }
                }}
            />
            <DirectReferralDialog
                open={openDirectReferral}
                onClose={() => {
                    // fetchPatients();
                    setOpenDirectReferral(false);
                    if (mtTableRef.current) {
                        // eslint-disable-next-line no-unused-expressions
                        mtTableRef.current.onQueryChange();
                    }
                }}
            />
            <Backdrop className={classes.backdrop} open={working} onClick={handleClose}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default PhioengagePatientList;
